<template>
  <ion-card class="border-default no-shadow spacing-xs filter-card ion-no-margin bg-white">
    <div class="d-flex align-center">
      <div
        class="info-btn"
        :style="`background-color: ${statusOptions[type as keyof typeof statusOptions].accent}`"
      >
        <ion-icon
          :icon="statusOptions[type as keyof typeof statusOptions].icon"
          :color="statusOptions[type as keyof typeof statusOptions].color"
          class="info-icon"
        ></ion-icon>
      </div>

      <p v-if="type === 'pending' && booking" class="font-sm px-xs">
        {{
          t('bookingDetails.hoursRemain', {
            hours: getRemainHours(booking.createdAt as string),
          })
        }}
      </p>

      <p v-if="type === 'canceled'" class="font-sm px-xs">
        {{
          t('bookingDetails.rejectingReason', {
            reason: getReason,
          })
        }}
      </p>

      <p v-if="type === 'accepted'" class="font-sm px-xs">
        {{ t('bookingDetails.pendingPayment') }}
      </p>

      <p v-if="type === 'check-in' || type === 'check-out'" class="font-sm px-xs">
        {{ t(`${type}.card`) }}
      </p>
    </div>

    <ion-button
      v-if="type === 'accepted'"
      @click="router.push({ name: 'Billing' })"
      fill="solid"
      expand="block"
      size="default"
      color="primary"
      class="text-lg text-weight-bold mt-xs w-100"
    >
      {{ t('invoices.payNow') }}
    </ion-button>

    <ion-button
      v-if="type === 'check-in'"
      @click="router.push({ name: 'CheckIn', params: { bookingId: booking?.id } })"
      fill="solid"
      expand="block"
      size="default"
      color="primary"
      class="text-lg text-weight-bold mt-xs w-100"
    >
      {{ t('check-in.cardBtn') }}
    </ion-button>

    <ion-button
      v-if="type === 'check-out'"
      @click="
        router.push({
          name: 'CheckOutReservation',
          params: { bookingId: booking?.id, rentalId: booking?.RentalUnit?.publicId },
        })
      "
      fill="solid"
      expand="block"
      size="default"
      color="primary"
      class="text-lg text-weight-bold mt-xs w-100"
    >
      {{ t('check-out.cardBtn') }}
    </ion-button>
  </ion-card>
</template>

<script setup lang="ts">
  import router from '@/router';
  import { computed } from 'vue';
  import { IonButton, IonCard, IonIcon } from '@ionic/vue';
  import { useI18n } from 'vue-i18n';
  import { airplaneOutline, alertCircle, cashOutline } from 'ionicons/icons';
  import { BookingForm } from '@/interfaces/booking/booking.interface';
  import { cancelReasonsOptions } from '@/utils/constants/general-constants';

  const props = withDefaults(
    defineProps<{
      booking?: BookingForm;
      type?: string;
    }>(),
    {},
  );

  const statusOptions = {
    pending: { accent: 'var(--ion-color-primary-tint)', color: 'primary', icon: alertCircle },
    accepted: { accent: 'var(--ion-color-success-tint)', color: 'success', icon: cashOutline },
    canceled: { accent: 'var(--ion-color-primary-tint)', color: 'primary', icon: alertCircle },
    'check-in': { accent: 'var(--ion-color-primary-tint)', color: 'primary', icon: alertCircle },
    'check-out': {
      accent: 'var(--ion-color-info-tint)',
      color: 'info',
      icon: airplaneOutline,
    },
  };

  const { t } = useI18n();

  const getReason = computed(() => {
    if (!props.booking) {
      return;
    }
    const { StatusHistory } = props.booking;

    if (!StatusHistory) {
      return t('cancelledReasonsItems.noReason');
    }

    const canceledHistory = StatusHistory.find(({ status }) => status === 'canceled');

    if (!canceledHistory) {
      return t('cancelledReasonsItems.noReason');
    }

    const messageCancellationId = Number(canceledHistory.messageCancellationId);
    const cancelOptions = cancelReasonsOptions();

    return (
      cancelOptions[messageCancellationId as keyof typeof cancelOptions] ??
      t('cancelledReasonsItems.noReason')
    );
  });

  const getRemainHours = (creationDate: string) => {
    const date = new Date(creationDate);
    const limitDate = new Date(date.getTime() + 48 * 60 * 60 * 1000);
    const now = new Date();

    const diff = limitDate.getTime() - now.getTime();

    if (diff <= 0) {
      return 0;
    }
    return Math.ceil(diff / (1000 * 60 * 60));
  };
</script>
